import { FC } from "react";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { useParams } from "react-router-dom";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import Button from "@/components/Button";
import useAlerts from "@/hooks/useAlerts";
import { FormLayout, Input, Label, Larger, Textarea } from "@/components/Forms";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import Note from "@/components/Forms/Note";

const StyledLabel = styled(Label)`
  text-transform: capitalize;
`;

interface Props {
  onClose: () => void;
  category: "script" | "thumb" | "title" | "type";
  title: string;
}

const TopicsCreationFormModal: FC<Props> = ({ onClose, category, title }) => {
  const { teamId } = useParams();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm();

  const alerts = useAlerts();

  const endpoint = "/v1/video-topics";

  const onSubmit = handleSubmit(async (data) => {
    try {
      await api.post(endpoint, {
        team_id: teamId === "all" ? null : teamId,
        title: data.title,
        category,
        ...(!!data.notes && { notes: data.notes }),
      });
      alerts.success(`${title} was successfully created`);
      onClose();
    } catch (err) {
      handleFormErrors(err, setError);
    }
  });

  return (
    <Modal>
      <SidePanel heading={title} onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Note>
                  New{" "}
                  {category === "type" ? "video type " : `${category} topic `}
                  will be added to system library from where it could be
                  assigned to videos.
                </Note>
              </Larger>
              <Larger>
                <StyledLabel>{title}</StyledLabel>
                <Input
                  {...register("title")}
                  placeholder={
                    category === "type"
                      ? "Add video type"
                      : `Add ${category} topic`
                  }
                  $hasError={!!errors.title}
                />
                {!!errors.title && (
                  <ValidationMessage>
                    {errors.title.message as string}
                  </ValidationMessage>
                )}
              </Larger>

              <Larger>
                <Label>Note</Label>
                <Textarea {...register("notes")} placeholder="Add notes..." />
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default TopicsCreationFormModal;
